import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Portal from '@mui/material/Portal';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { sendBulkSms, BulkSmsSendResult } from '../../api/bulkSms';
import { TypeableDateField, TypeableTimeField } from './components/TypeablePickerField';

dayjs.extend(utc);

const MAX_MESSAGE_LENGTH = 146;
const EAT_OFFSET_HOURS = 3;
const PROCESS_AT_FORMAT = 'YYYY-MM-DDTHH:mm';
const DISPLAY_FORMAT = 'ddd D MMM YYYY, HH:mm';
// Current Kenya wall-clock time as a local dayjs, so pickers compare correctly
// regardless of the browser's timezone. Kenya has no DST, so a fixed +3 holds.
function eatNow(): Dayjs {
  return dayjs(dayjs().utc().add(EAT_OFFSET_HOURS, 'hour').format('YYYY-MM-DDTHH:mm:ss'));
}

// Response processAt is treated as UTC and shown in EAT.
function formatProcessAt(value: string): string {
  return dayjs.utc(value).add(EAT_OFFSET_HOURS, 'hour').format(DISPLAY_FORMAT);
}
const BRAND_GRADIENT = 'linear-gradient(180deg, #5136F2 0%, #100C2E 100%)';

const STEPS = [
  { label: 'Upload your list', detail: 'A CSV with one phone number per row, no header.' },
  { label: 'Compose your message', detail: 'Keep it under 146 characters for a single SMS segment.' },
  { label: 'Review & confirm', detail: 'We validate every number before anything goes out.' },
];

function IntroPanel() {
  return (
    <Box
      sx={{
        background: BRAND_GRADIENT,
        color: '#fff',
        borderRadius: { xs: 3, md: '16px 0 0 16px' },
        p: { xs: 3, md: 4 },
        minHeight: { md: 480 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -60,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.06)',
        }}
      />
      <Box sx={{ position: 'relative' }}>
        <Chip
          icon={<CampaignRoundedIcon sx={{ color: '#fff !important' }} />}
          label="Marketing"
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontWeight: 600,
            mb: 3,
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Bulk SMS
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 320 }}>
          Reach every client on a CSV list with one broadcast. We handle validation,
          deduplication and delivery tracking for you.
        </Typography>
      </Box>

      <Stack spacing={2.5} sx={{ position: 'relative', mt: 4 }}>
        {STEPS.map((step, i) => (
          <Stack key={step.label} direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                {step.detail}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mt: 3,
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.1)',
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          Numbers need a country code, e.g. <strong>+254700000000</strong>.
        </Typography>
      </Box>
    </Box>
  );
}

function CsvUploader({ file, onFile, onRemove }: {
  file: File | null;
  onFile: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        textAlign: 'center',
        borderStyle: 'dashed',
        borderWidth: 1.5,
        borderRadius: 2,
        bgcolor: file ? 'action.hover' : 'transparent',
        cursor: file ? 'default' : 'pointer',
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': file ? undefined : { borderColor: '#5136F2', bgcolor: 'rgba(81,54,242,0.04)' },
      }}
      onClick={() => {
        if (!file) inputRef.current?.click();
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (selected) onFile(selected);
          e.target.value = '';
        }}
      />
      {file ? (
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
          <CheckCircleRoundedIcon color="success" fontSize="small" />
          <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
          <Button size="small" color="error" onClick={onRemove}>Remove</Button>
        </Stack>
      ) : (
        <Stack spacing={1} alignItems="center">
          <UploadFileRoundedIcon sx={{ color: '#5136F2' }} />
          <Typography variant="body2" color="text.secondary">
            Click to upload CSV (one phone number per row, no header)
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}

export default function BulkSmsPage() {
  const [file, setFile] = React.useState<File | null>(null);
  const [message, setMessage] = React.useState('');
  const [mode, setMode] = React.useState<'instant' | 'schedule'>('instant');
  const [scheduleDate, setScheduleDate] = React.useState<Dayjs | null>(null);
  const [scheduleTime, setScheduleTime] = React.useState<Dayjs | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<BulkSmsSendResult | null>(null);
  const [toast, setToast] = React.useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const showToast = (severity: 'success' | 'error', message: string) => {
    setToast({ open: true, severity, message });
  };

  const handleCloseToast = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const charCount = message.length;
  const overLimit = charCount > MAX_MESSAGE_LENGTH;
  const isScheduled = mode === 'schedule';
  const scheduleComplete = !!scheduleDate?.isValid() && !!scheduleTime?.isValid();
  const processAt = isScheduled && scheduleComplete
    ? `${scheduleDate!.format('YYYY-MM-DD')}T${scheduleTime!.format('HH:mm')}`
    : undefined;
  const scheduleInPast = !!processAt && processAt <= eatNow().format(PROCESS_AT_FORMAT);
  const scheduleValid = !isScheduled || (!!processAt && !scheduleInPast);
  const canSubmit = !!file && message.trim().length > 0 && !overLimit && scheduleValid;
  const now = eatNow();
  const minTime = scheduleDate?.isSame(now, 'day') ? now : undefined;
  const scheduledLabel = processAt ? dayjs(processAt).format(DISPLAY_FORMAT) : '';

  const handleConfirmSend = async () => {
    if (!file) return;
    if (isScheduled && (!processAt || processAt <= eatNow().format(PROCESS_AT_FORMAT))) {
      setConfirmOpen(false);
      setError('The scheduled time has passed. Pick a time in the future.');
      return;
    }
    setLoading(true);
    setError(null);
    const res = await sendBulkSms(file, message, processAt);
    setLoading(false);
    setConfirmOpen(false);
    if (!res.data) {
      setError(res.message ?? 'Failed to send bulk SMS.');
      return;
    }
    setResult(res.data);

    const recipients = `${res.data.sentCount} number${res.data.sentCount === 1 ? '' : 's'}`;
    if (res.data.accepted) {
      showToast(
        'success',
        isScheduled
          ? `Message scheduled for ${recipients} at ${scheduledLabel} EAT.`
          : `Message sent to ${recipients}.`,
      );
    } else {
      const reason = res.data.invalidNumbers[0]?.reason ?? res.data.responseDesc;
      showToast('error', reason);
    }
  };

  const resetForm = () => {
    setFile(null);
    setMessage('');
    setMode('instant');
    setScheduleDate(null);
    setScheduleTime(null);
    setResult(null);
    setError(null);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1100 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
        <Box sx={{ width: { xs: '100%', md: '38%' } }}>
          <IntroPanel />
        </Box>

        <Box sx={{ flex: 1, p: { xs: 3, md: 4 } }}>
          {!result && (
            <Stack spacing={3}>
              <CsvUploader file={file} onFile={setFile} onRemove={() => setFile(null)} />

              <TextField
                label="Message"
                multiline
                rows={5}
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={overLimit}
                helperText={`${charCount}/${MAX_MESSAGE_LENGTH} characters${overLimit ? ' — message too long' : ''}`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    alignItems: 'flex-start',
                    padding: '12px',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: 0,
                  },
                }}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>Delivery</Typography>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => setMode(e.target.value as 'instant' | 'schedule')}
                >
                  <FormControlLabel value="instant" control={<Radio />} label="Send now" />
                  <FormControlLabel value="schedule" control={<Radio />} label="Schedule" />
                </RadioGroup>

                {isScheduled && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                      <DatePicker
                        label="Date"
                        value={scheduleDate}
                        onChange={setScheduleDate}
                        minDate={now}
                        format="DD/MM/YYYY"
                        slots={{ field: TypeableDateField }}
                      />
                      <TimePicker
                        label="Time (24h)"
                        value={scheduleTime}
                        onChange={setScheduleTime}
                        ampm={false}
                        format="HH:mm"
                        minTime={minTime}
                        slots={{ field: TypeableTimeField }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color={scheduleInPast ? 'error' : 'text.secondary'}
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {scheduleInPast
                        ? 'That time has already passed in Kenya. Pick a time in the future.'
                        : 'Kenya time (EAT). Type it in or use the picker.'}
                    </Typography>
                  </LocalizationProvider>
                )}
              </Box>

              <Button
                variant="contained"
                size="large"
                disabled={!canSubmit}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  alignSelf: 'flex-start',
                  px: 4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#fff',
                  background: BRAND_GRADIENT,
                  '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.08)' },
                  '&.Mui-disabled': { background: BRAND_GRADIENT, color: 'rgba(255,255,255,0.5)', opacity: 0.5 },
                }}
              >
                {isScheduled ? 'Review & Schedule' : 'Review & Send'}
              </Button>
            </Stack>
          )}

          {result && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  label={result.accepted ? (isScheduled ? 'Scheduled' : 'Accepted') : 'Rejected'}
                  color={result.accepted ? 'success' : 'error'}
                />
                <Typography variant="body2" color="text.secondary">{result.responseDesc}</Typography>
              </Stack>

              {isScheduled && result.accepted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Scheduled for{' '}
                  <strong>
                    {result.processAt ? formatProcessAt(result.processAt) : scheduledLabel} EAT
                  </strong>
                  . Numbers below are queued and will go out at that time.
                </Alert>
              )}

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Total rows</Typography>
                  <Typography variant="h6">{result.totalRows}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">{isScheduled ? 'Queued' : 'Sent'}</Typography>
                  <Typography variant="h6" color="success.main">{result.sentCount}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Duplicates</Typography>
                  <Typography variant="h6">{result.duplicateCount}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Invalid</Typography>
                  <Typography variant="h6" color="error.main">{result.invalidCount}</Typography>
                </Grid>
              </Grid>

              {result.invalidNumbers.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Invalid rows</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Line</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.invalidNumbers.map((row) => (
                          <TableRow key={`${row.lineNumber}-${row.value}`}>
                            <TableCell>{row.lineNumber}</TableCell>
                            <TableCell>{row.value}</TableCell>
                            <TableCell>{row.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={resetForm}>Send another</Button>
                <Link
                  href={isScheduled ? '/bulk-sms/history?batchType=SCHEDULED' : '/bulk-sms/history'}
                  underline="hover"
                  sx={{ alignSelf: 'center' }}
                >
                  {isScheduled ? 'View scheduled messages' : 'View delivery status'}
                </Link>
              </Stack>
            </Box>
          )}
        </Box>
        </Paper>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isScheduled ? 'Confirm scheduled broadcast' : 'Confirm broadcast'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            You are about to {isScheduled ? 'schedule' : 'send'} this message to every valid number in{' '}
            <strong>{file?.name}</strong>. Invalid, duplicate, or ineligible numbers will be
            skipped and reported after {isScheduled ? 'scheduling' : 'sending'}.
          </Typography>
          {isScheduled && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Goes out <strong>{scheduledLabel} EAT</strong>
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2, mt: 2, whiteSpace: 'pre-wrap' }}>
            <Typography variant="body2">{message}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmSend}
            disabled={loading}
            sx={{
              color: '#fff',
              background: BRAND_GRADIENT,
              '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.08)' },
              '&.Mui-disabled': { background: BRAND_GRADIENT, color: 'rgba(255,255,255,0.5)', opacity: 0.5 },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : isScheduled ? 'Schedule broadcast' : 'Send broadcast'}
          </Button>
        </DialogActions>
      </Dialog>

      <Portal>
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ zIndex: 9999 }}
        >
          <Alert
            onClose={handleCloseToast}
            severity={toast.severity}
            variant="filled"
            sx={(theme) => {
              const isSuccess = toast.severity === 'success';
              const isDark = theme.palette.mode === 'dark';
              const bg = isSuccess
                ? isDark ? 'hsl(122, 39%, 22%)' : 'hsl(122, 39%, 30%)'
                : isDark ? 'hsl(0, 65%, 28%)' : 'hsl(0, 65%, 35%)';
              const borderColor = isSuccess
                ? isDark ? 'hsl(122, 39%, 42%)' : 'hsl(122, 39%, 48%)'
                : isDark ? 'hsl(0, 65%, 48%)' : 'hsl(0, 65%, 52%)';
              return {
                bgcolor: bg,
                border: '1px solid',
                borderColor,
                color: '#fff !important',
                boxShadow: theme.shadows[4],
                '& .MuiAlert-icon, & .MuiAlert-action, & .MuiAlert-message': {
                  color: '#fff !important',
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  color: 'rgba(255,255,255,0.8) !important',
                  backgroundColor: 'transparent !important',
                  '&:hover': { backgroundColor: 'transparent !important' },
                },
              };
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Box>
  );
}
